import { z } from 'zod';
import { servicePlaceSchema } from '../people/details.js';
/**
 * The place picker's three server routes (ADR-018 §3 choice B, #473 part A).
 *
 * WHAT CROSSES THE WIRE. The browser sends the letters HR typed, the kind of place, and a session
 * token it made up. It sends no name, email, phone or record id, and the schemas below have no field
 * for one (`.strict()` refuses an extra). Place ids travel in the address of the names read: a place id
 * is Google's code for a city or ZIP code, not a person's data, and it is limited to the characters
 * Google uses so it cannot carry anything else.
 */
/** Google's place ids are URL-safe text. Anything else is refused before it reaches a URL. */
export const PLACE_ID_SHAPE = /^[A-Za-z0-9_-]{1,300}$/;
export const placeIdSchema = z.string().regex(PLACE_ID_SHAPE, 'Not a place id');
export const placeKindSchema = servicePlaceSchema.shape.kind;
/**
 * Made up by the browser for one search-and-pick, and passed to Google so the typing and the pick bill
 * as one (ADR-018 §6). A UUID, so it can carry nothing else.
 */
export const placeSessionTokenSchema = z.string().uuid();
/** Fewer than two letters is a request that cannot name a city, and costs a billed call. */
export const PLACE_INPUT_MIN = 2;
/**
 * The longest a city or ZIP code needs: the longest Virginia city names are about 15 characters and a ZIP+4 is 10,
 * so 40 leaves room for "City, VA". Anything longer is not a place, and what is typed goes to Google.
 */
export const PLACE_INPUT_MAX = 40;
export const placeAutocompleteRequestSchema = z.object({
    // No "@": an email address is the one kind of personal text that is cheap to spot, and it must never go to Google.
    // Names cannot be told from cities by a rule, so nothing tries (ADR-018 §4: the box says what it is for).
    input: z.string().trim().min(PLACE_INPUT_MIN).max(PLACE_INPUT_MAX).refine((value) => !value.includes('@'), 'Enter only a city or ZIP code.'),
    kind: placeKindSchema,
    sessionToken: placeSessionTokenSchema,
}).strict();
/**
 * One suggestion. `text` is Google's words, shown while HR is choosing and kept nowhere (ADR-018 §1:
 * a place's name is never stored).
 */
export const placeSuggestionSchema = z.object({ placeId: placeIdSchema, text: z.string().max(300) }).strict();
export const placeAutocompleteResponseSchema = z.object({ suggestions: z.array(placeSuggestionSchema).max(10) }).strict();
/** Picking one. The answer is a `servicePlace` ready to put on the record, or a refusal. */
export const placeResolveRequestSchema = z.object({
    placeId: placeIdSchema,
    kind: placeKindSchema,
    sessionToken: placeSessionTokenSchema,
}).strict();
/**
 * `name` is the words for the place (a city's name, a ZIP code's number), made by the same function the names read uses from the
 * address pieces the pick already read, so the pill and the Roster tag agree. It is NOT stored and no Google field is added for it.
 * Optional: a client without it falls back to the suggestion's own words.
 */
export const placeResolveResponseSchema = z.object({ place: servicePlaceSchema, name: z.string().max(200).optional() }).strict();
/** The most distinct places one roster read asks about. */
export const PLACE_NAMES_MAX = 50;
/** The names read: `?ids=a,b,c`, distinct, at most `PLACE_NAMES_MAX`. */
export const placeNamesQuerySchema = z
    .string()
    .max(PLACE_NAMES_MAX * 301)
    .transform((raw) => raw.split(',').map((part) => part.trim()).filter((part) => part !== ''))
    .pipe(z.array(placeIdSchema).min(1).max(PLACE_NAMES_MAX))
    .transform((ids) => [...new Set(ids)]);
/** Why a name is not there. Closed, so the screen can say "Place name unavailable" without guessing. */
export const placeNameStateSchema = z.enum(['ok', 'not-found', 'unavailable']);
export const placeNameSchema = z.object({
    placeId: placeIdSchema,
    state: placeNameStateSchema,
    /** Present only when `state` is `ok`. Held by the page while it is open; never stored. */
    name: z.string().max(200).optional(),
}).strict();
export const placeNamesResponseSchema = z.object({ names: z.array(placeNameSchema).max(PLACE_NAMES_MAX) }).strict();
/** The plain wording both screens and the API use when Google cannot be reached (ADR-018 §5). */
export const PLACES_UNREACHABLE_MESSAGE = 'Google cannot be reached, so a place cannot be added right now. You can still save the rest of this person’s details.';
export const PLACES_NOT_VIRGINIA_MESSAGE = 'That is not a Virginia city or ZIP code.';
export const PLACES_RATE_LIMITED_MESSAGE = 'Too many searches just now. Wait a moment and try again.';
//# sourceMappingURL=index.js.map